<script setup>
import { ref, computed } from 'vue'
import { Link, router, usePage } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import AppLayout from '@/Layouts/AppLayout.vue'
import PageShell from '@/Components/UI/PageShell.vue'
import EmptyState from '@/Components/UI/EmptyState.vue'
import LayoutToggle from '@/Components/UI/LayoutToggle.vue'
import Icon from '@/Components/UI/Icon.vue'
import UiButton from '@/Components/UI/UiButton.vue'
import UiInput from '@/Components/UI/UiInput.vue'
import CustomerCard from '@/Components/Cards/CustomerCard.vue'
import { useCurrency } from '@/composables/useCurrency.js'
import { useLayoutMode } from '@/composables/useLayoutMode.js'
import { setPageHeaderCount } from '@/composables/usePageHeader.js'

const LAYOUT_MODES = [
  { v: 'table',   label: 'Table',   icon: 'layout-table' },
  { v: 'cards',   label: 'Cards',   icon: 'layout-cards' },
  { v: 'compact', label: 'Compact', icon: 'layout-list'  },
]

defineOptions({ layout: AppLayout })
const { t } = useI18n()
const { formatDate } = useCurrency()

const props = defineProps({
  customers: Array,
  meta:      Object,
  filters:   Object,
})

const page      = usePage()
const canCreate = computed(() => page.props.auth?.user?.role !== 'viewer')
const isAdmin   = computed(() => page.props.auth?.user?.role === 'admin')

const search       = ref(props.filters?.q || '')
const showArchived = ref(props.filters?.archived === '1' || false)
const layoutMode   = useLayoutMode('customers', LAYOUT_MODES, 'table')

let searchTimer = null
const onSearch = () => {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(() => {
    router.get('/customers', {
      q: search.value,
      archived: showArchived.value ? '1' : ''
    }, { preserveState: true })
  }, 200)
}

const LOCALE_CODES = { en: 'EN', ru: 'RU', th: 'TH', zh: 'ZH' }
const localeCode = (code) => code ? (LOCALE_CODES[code] || code.toUpperCase()) : null

const licenceStatus = (c) => {
  if (!c.licence_number) return { label: t('customers.licence.none'), cls: 'bg-gray-100 text-gray-500' }
  if (!c.licence_expiry) return { label: t('customers.licence.valid'), cls: 'bg-green-50 text-green-700' }
  const exp = new Date(c.licence_expiry)
  const now = new Date()
  const daysLeft = Math.ceil((exp - now) / 86400000)
  if (daysLeft < 0)  return { label: t('customers.licence.expired'), cls: 'bg-red-100 text-red-700' }
  if (daysLeft < 30) return { label: t('customers.licence.expiring', { days: daysLeft }), cls: 'bg-amber-50 text-amber-700' }
  return { label: t('customers.licence.valid'), cls: 'bg-green-50 text-green-700' }
}

const customersWithStatus = computed(() =>
  (props.customers || []).map(c => ({ ...c, _licenceStatus: licenceStatus(c) }))
)

setPageHeaderCount(computed(() => props.meta?.total ?? 0), (n) => t('customers.count', { n }).replace(/^\d+\s*/, ''))
</script>

<template>
  <PageShell
    :back-href="filters?.from === 'dashboard' ? '/' : ''"
    :back-label="t('common.backToDashboard', 'Dashboard')"
    :title="t('customers.title', 'Customers')"
  >
    <template #actions>
      <Link
        v-if="canCreate"
        href="/customers/new"
      >
        <UiButton
          variant="primary"
          icon-left="plus"
        >
          {{ t('customers.new') }}
        </UiButton>
      </Link>
    </template>

    <template #filters>
      <UiInput
        v-model="search"
        type="search"
        icon-left="search"
        :placeholder="t('customers.searchPlaceholder')"
        class="flex-1 min-w-[12rem] max-w-sm"
        @update:model-value="onSearch"
      />
      <label
        v-if="isAdmin"
        class="inline-flex items-center gap-2 text-sm text-[color:var(--inkSoft)] cursor-pointer select-none"
      >
        <input
          v-model="showArchived"
          type="checkbox"
          class="rounded border-[color:var(--line)] text-[color:var(--accent)] focus:ring-[color:var(--accent)]"
          @change="onSearch"
        >
        {{ t('customers.showArchived') }}
      </label>
      <div class="ml-auto">
        <LayoutToggle
          v-model="layoutMode"
          :modes="LAYOUT_MODES"
        />
      </div>
    </template>

    <!-- Table -->
    <div v-if="layoutMode === 'table'">
      <!-- Mobile: card stack -->
      <div class="md:hidden space-y-3">
        <EmptyState
          v-if="!customers?.length"
          :type="search ? 'filter' : 'first-use'"
          :title="search ? t('customers.emptySearch', { q: search }) : t('customers.emptyFirst')"
          :message="search ? t('customers.emptySearchHint') : t('customers.emptyFirstHint')"
          @clear="search = ''; onSearch()"
        />
        <Link
          v-for="c in customersWithStatus"
          :key="c.id"
          :href="`/customers/${c.id}`"
          class="block rounded-xl p-4 transition-colors"
          :style="{ background: 'var(--surface)', border: '1px solid var(--line)' }"
          :class="c.archived_at ? 'opacity-60' : ''"
        >
          <CustomerCard :customer="c" />
        </Link>
      </div>

      <!-- md+: existing table (verbatim, just wrapped) -->
      <div
        class="hidden md:block rounded-xl overflow-hidden"
        :style="{ background: 'var(--surface)', border: '1px solid var(--line)' }"
      >
        <table
          class="w-full text-sm"
          role="grid"
        >
          <thead :style="{ background: 'var(--surfaceAlt)', borderBottom: '1px solid var(--line)' }">
            <tr>
              <th
                scope="col"
                class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[color:var(--muted)]"
              >
                Name
              </th>
              <th
                scope="col"
                class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[color:var(--muted)] hidden md:table-cell"
              >
                Phone / Email
              </th>
              <th
                scope="col"
                class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[color:var(--muted)]"
              >
                Licence
              </th>
              <th
                scope="col"
                class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[color:var(--muted)] hidden md:table-cell"
              >
                {{ t('customers.index.language') }}
              </th>
              <th
                scope="col"
                class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[color:var(--muted)] hidden lg:table-cell"
              >
                Rentals
              </th>
              <th
                scope="col"
                class="px-4 py-3 w-10"
              >
                <span class="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(c, i) in customersWithStatus"
              :key="c.id"
              class="transition-colors hover:bg-[var(--surfaceAlt)]"
              :style="{ borderTop: i === 0 ? 'none' : '1px solid var(--line)' }"
              :class="c.archived_at ? 'opacity-60' : ''"
            >
              <td class="px-4 py-3">
                <Link
                  :href="`/customers/${c.id}`"
                  class="font-medium text-[color:var(--ink)] hover:text-[color:var(--accent)] transition-colors"
                >
                  {{ c.first_name }} {{ c.last_name }}
                </Link>
                <span
                  v-if="c.archived_at"
                  class="ml-2 text-xs text-[color:var(--muted)] italic"
                >{{ t('customers.archivedLabel') }}</span>
              </td>
              <td class="px-4 py-3 hidden md:table-cell">
                <a
                  v-if="c.phone"
                  :href="`tel:${c.phone}`"
                  class="block text-sm text-[color:var(--inkSoft)] hover:text-[color:var(--accent)] transition-colors"
                >{{ c.phone }}</a>
                <span class="text-xs text-[color:var(--muted)]">{{ c.email }}</span>
              </td>
              <td class="px-4 py-3">
                <span
                  class="inline-flex px-2 py-0.5 rounded-full text-xs font-medium"
                  :class="c._licenceStatus.cls"
                >
                  {{ c._licenceStatus.label }}
                </span>
                <span
                  v-if="c.licence_expiry"
                  class="block text-xs text-[color:var(--muted)] mt-0.5"
                >
                  {{ formatDate(c.licence_expiry) }}
                </span>
              </td>
              <td class="px-4 py-3 hidden md:table-cell">
                <span
                  v-if="localeCode(c.preferred_locale)"
                  class="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold tabular-nums tracking-wider"
                  :style="{ background: 'var(--surfaceAlt)', color: 'var(--inkSoft)', border: '1px solid var(--line)' }"
                >
                  {{ localeCode(c.preferred_locale) }}
                </span>
                <span
                  v-else
                  class="text-xs text-[color:var(--muted)]"
                  style="font-variant-numeric: normal"
                >—</span>
              </td>
              <td class="px-4 py-3 hidden lg:table-cell text-[color:var(--inkSoft)]">
                {{ c.rentals_count ?? 0 }} rentals
              </td>
              <td class="px-4 py-3">
                <Link
                  :href="`/customers/${c.id}`"
                  class="text-[color:var(--accent)] hover:underline text-xs font-medium"
                >
                  {{ t('common.view') }}
                </Link>
              </td>
            </tr>
          </tbody>
        </table>
        <EmptyState
          v-if="!customers?.length"
          :type="search ? 'filter' : 'first-use'"
          :title="search ? t('customers.emptySearch', { q: search }) : t('customers.emptyFirst')"
          :message="search ? t('customers.emptySearchHint') : t('customers.emptyFirstHint')"
          @clear="search = ''; onSearch()"
        />
      </div>
    </div>

    <!-- Cards view -->
    <div v-else-if="layoutMode === 'cards'">
      <EmptyState
        v-if="!customers?.length"
        :type="search ? 'filter' : 'first-use'"
        :title="search ? t('customers.emptySearch', { q: search }) : t('customers.emptyFirst')"
        :message="search ? t('customers.emptySearchHint') : t('customers.emptyFirstHint')"
        @clear="search = ''; onSearch()"
      />
      <div
        v-else
        class="grid gap-3"
        style="grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));"
      >
        <Link
          v-for="c in customersWithStatus"
          :key="c.id"
          :href="`/customers/${c.id}`"
          class="block rounded-xl p-4 transition-colors"
          :style="{ background: 'var(--surface)', border: '1px solid var(--line)' }"
          :class="c.archived_at ? 'opacity-60' : ''"
        >
          <CustomerCard :customer="c" />
        </Link>
      </div>
    </div>

    <!-- Compact view -->
    <div
      v-else-if="layoutMode === 'compact'"
      class="rounded-xl overflow-hidden"
      :style="{ background: 'var(--surface)', border: '1px solid var(--line)' }"
    >
      <EmptyState
        v-if="!customers?.length"
        :type="search ? 'filter' : 'first-use'"
        :title="search ? t('customers.emptySearch', { q: search }) : t('customers.emptyFirst')"
        :message="search ? t('customers.emptySearchHint') : t('customers.emptyFirstHint')"
        @clear="search = ''; onSearch()"
      />
      <ul
        v-else
        role="list"
      >
        <li
          v-for="(c, i) in customersWithStatus"
          :key="c.id"
          :style="{ borderTop: i === 0 ? 'none' : '1px solid var(--line)' }"
        >
          <Link
            :href="`/customers/${c.id}`"
            class="flex items-center gap-3 px-4 py-2 transition-colors hover:bg-[var(--surfaceAlt)]"
            :class="c.archived_at ? 'opacity-60' : ''"
          >
            <span
              class="text-[0.8125rem] font-medium truncate flex-1"
              style="color: var(--ink);"
            >{{ c.first_name }} {{ c.last_name }}</span>
            <span
              class="text-[0.75rem] truncate hidden md:inline"
              style="color: var(--muted); max-width: 180px;"
            >{{ c.phone || c.email }}</span>
            <span
              v-if="localeCode(c.preferred_locale)"
              class="text-[0.625rem] font-semibold tracking-wider px-1.5 py-0.5 rounded-full"
              :style="{ background: 'var(--surfaceAlt)', color: 'var(--inkSoft)', border: '1px solid var(--line)' }"
            >{{ localeCode(c.preferred_locale) }}</span>
            <span
              class="mono text-[0.6875rem] tabular-nums"
              style="color: var(--inkSoft);"
            >{{ c.rentals_count ?? 0 }}</span>
            <Icon
              name="chev-r"
              :size="14"
              class="opacity-40"
            />
          </Link>
        </li>
      </ul>
    </div>

    <!-- Pagination -->
    <div
      v-if="meta?.total_pages > 1"
      class="flex items-center justify-between text-sm text-[color:var(--inkSoft)]"
    >
      <p>Page {{ meta.current_page }} of {{ meta.total_pages }}</p>
      <div class="flex gap-2">
        <Link
          v-if="meta.current_page > 1"
          :href="`/customers?page=${meta.current_page - 1}&q=${search}${showArchived ? '&archived=1' : ''}`"
          class="px-3 py-1.5 rounded-[var(--radius-md)] border border-[color:var(--line)] bg-[color:var(--surface)] hover:bg-[color:var(--surfaceAlt)] transition-colors"
        >
          Previous
        </Link>
        <Link
          v-if="meta.current_page < meta.total_pages"
          :href="`/customers?page=${meta.current_page + 1}&q=${search}${showArchived ? '&archived=1' : ''}`"
          class="px-3 py-1.5 rounded-[var(--radius-md)] border border-[color:var(--line)] bg-[color:var(--surface)] hover:bg-[color:var(--surfaceAlt)] transition-colors"
        >
          Next
        </Link>
      </div>
    </div>
  </PageShell>
</template>
